import { OfflineButton } from '@/components/studio/OfflineNotice';
import quickTools from '@/components/quicktools/tools.json';
import { QuickToolCards, useQuickToolPrices } from '@/components/quicktools/QuickToolCards';
import { SpotifyLoopBand } from '@/components/home/SpotifyLoopBand';
import { money, type QuickToolKey } from '@/lib/quicktools-api';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { ArrowRight, AudioWaveform, BadgeCheck, Captions, Check, Clapperboard, Coins, Film, Gift, Lock, Mic2, Repeat, Scissors, Sparkles } from 'lucide-react';
import { ChopBoxVisual } from './ChopBoxPitch';
import { useTranslation } from 'react-i18next';
import { PromoImg } from '@/components/storybook/marketing/PromoImg';
import { APPROVED_DRAWING, SAMPLE_PHOTOS } from '@/components/storybook/marketing/promo';
import { INTRO_PRICE } from '@/components/landing/WhyCorridoSection';
import type { ToolId } from '@/components/landing/ToolCompass';
import { Magnetic } from '@/components/fx/Magnetic';
import { useTilt } from '@/components/fx/useTilt';
import { useBentoReveal, whenRevealed } from '@/components/landing/immersive/BentoReveal';
import { afterScrollSettles, isPageScrolling, useScrollCalm, whenIdle } from '@/components/landing/immersive/BentoScroll';
import { useBentoWarmup } from '@/components/landing/immersive/BentoWarm';
import { playBentoWipe, type BentoWipe } from '@/components/landing/immersive/BentoWipe';
import { ADS_FALLBACK_CONFIG, type AdsConfig } from '@/lib/ads';
import { hasFinePointer, prefersReducedMotion } from '@/lib/motion';
import './ToolChooser.css';

// ToolChooser — the compass's WELCOME stop (2026-09-24). The landing page used
// to open straight into the corrido studio, so a parent looking for the kids'
// storybook never saw it. Now the first screen asks what you came to make and
// shows every tool as a card; picking one does exactly what picking that stop
// on the compass does (HeroSection owns the switch and the analytics).
//
// Bento layout (immersive redesign, 2026-09-24, structure from the owner's
// dashboard.html): a visible section header, then corrido as the wide lead
// card, TikTok story beside it, and ads / storybook / rap underneath. Every
// card reads the same way — tag chip, title, one-line pitch, chips, a meta or
// stats row, a full-width call to action. Tablets get two columns; phones get
// their own design: full-width cards with the picture on top, each short
// enough to fit one screen, and nothing hidden.
//
// Motion: cards rise and fade in as they scroll in (useBentoReveal). With a
// mouse they lean toward the pointer with a glare that follows it, an accent
// spotlight and orbiting ring, and a magnetic CTA; a finger gets a press
// instead. Picking a card opens a disc in the card's colour from the tap
// point, names the tool on it while the stop switches underneath, and
// dissolves into the new stop (playBentoWipe). Reduced motion gets none of it (the stop switches at
// once), and the finished layout is what shows whenever the animation cannot
// run.
//
// REAL OUTPUT, NOT SKETCHES (owner, 2026-09-24: "all of this should be
// represented with images and video clips"). Every clip below came out of the
// tool its card sells, and nothing else is shown as if it had:
//   * corrido — the screened bélico clip the owner put first (Guadalajara
//     sign), the same file the corrido pitch plays;
//   * TikTok story — the story engine's own captioned render, the clip the
//     story pitch plays;
//   * ads — a real ads-worker job: the owner's FaceShot-ChopShop energy-drink
//     can as the product photo, so the sample ad promotes the studio itself.
//     The can was cut out of the owner's picture before upload, so the
//     picture's AI watermark never reached the ad;
//   * storybook (2026-09-27) — the storybook engine's own pictures: two
//     SAMPLE photos (synthetic people of a made-up family, never customers,
//     always labelled "Sample photos") and the cover it drew from them. They
//     replaced the emoji book mockups, which never showed the point of the
//     product: your child's own face in the story. Only the one drawing the
//     owner approved as a clear likeness is shown (2026-09-28, promo.ts), so
//     the fan of the other art styles behind the cover is gone.
// Rap keeps its drawn teaser (that engine does not exist yet).
//
// Clips play muted, only while on screen, and never for a visitor who asked
// for reduced motion: they get the poster frame. They load quietly while the
// bento is still a screen away and start one at a time after their card has
// arrived and the page has stopped scrolling (see CardClip).
//
// Prices only where the site already states them: corrido's intro price comes
// from the same constant the corrido pitch uses, the ads card quotes the same
// credit ladder the ads pitch reads (the bundled fallback of the live price
// list, since the welcome stop is shown signed out), and the storybook says
// "free preview" because its own card does. The feature chips restate claims
// each tool's own pitch already makes. Nothing is invented here: no render
// counts, no uptime, no ratings.

// 'quick' (2026-10-16): the five pay-per-file quick tools, a card of their
// own that opens /tools (owner: "present them in a better way than to have
// them hidden in a little tab").
type ChooserToolId = Exclude<ToolId, 'welcome'> | 'quick';

interface CardDef {
  id: ChooserToolId;
  nameKey: string;
  /** The tool's studio tag from the compass (the chip above the title). */
  tagKey?: string;
  /** "r g b" of the tool's compass accent, for the ring, glow and CTA. */
  rgb: string;
  /** Grid placement in the bento. */
  span: string;
  /** Card direction per breakpoint (column on phones: media on top). */
  dir: string;
  /** The media box: phones (a 16:10 band), tablet, desktop. */
  media: string;
  /** Extra classes for the copy column. */
  body: string;
  /** Title size overrides for the bigger cards. */
  title?: string;
  isNew?: boolean;
  comingSoon?: boolean;
  /** Its tool cannot run right now (lib/tool-availability): greyed, locked. */
  offline?: boolean;
  lead?: boolean;
  /** The storybook is the one bright card: cream paper, dark ink. */
  light?: boolean;
}

// Desktop rhythm 4|2, 3|3, 6: corrido leads beside the TikTok phone; ads and
// the storybook share the second row as side-by-side cards (the ads credit
// ladder needs the width, and would otherwise stretch three cards); rap is a
// slim locked strip underneath. Tablets: corrido across the top, then pairs.
//
// Phones (2026-09-24 critique): every card has to fit one ~600px screen, so
// the picture, the pitch and the button are seen together instead of the
// bento reading as a feed. Each picture is the same 16:10 band (rap a slimmer
// strip), with tighter padding and chips; nothing is dropped.
//
// Tablets: TikTok and ads sit side by side and their buttons line up at the
// foot. The ads card is the taller one (its credit ladder), so the TikTok
// picture takes the spare height: a bigger rising phone rather than the ~80px
// hole that opened between its chips and its footer (round 2 critique,
// 2026-09-24).
//
// The six-column desktop bento starts at xl, not lg (round 3 critique,
// 2026-09-25): from 1024 to 1279 (iPad landscape, small laptops) its ads
// column was 170 px, narrower than the phone in it, the corrido title took
// three lines and its clip lost the sign's first letter. That range keeps the
// two-column tablet bento, which has the room.
//
// Desktop rows are as tall as their tallest card, and the copy of the
// others took the difference as one empty band (round 3: 101 px in the
// corrido card, 85 px in ads). So the pictures that can grow do the
// growing: the TikTok and storybook bands are only as tall as a minimum that
// lets their row neighbour set the height, then fill whatever is left.
const PHONE_BODY = 'px-[18px] pb-5 pt-[18px]';
const CARDS: CardDef[] = [
  // site amber, 40 88% 64%
  {
    id: 'corrido', nameKey: 'compass.corrido', tagKey: 'compass.corridoTag', rgb: '244 190 82', lead: true,
    span: 'sm:col-span-2 xl:col-span-4',
    dir: 'sm:flex-row-reverse',
    media: 'aspect-[16/10] w-full border-b sm:aspect-auto sm:w-[44%] sm:min-h-[380px] sm:border-b-0 sm:border-l',
    body: `${PHONE_BODY} sm:flex-1 sm:p-8 lg:p-10 lg:pb-6`,
  },
  // TikTok cyan #25F4EE
  {
    id: 'story', nameKey: 'compass.story', tagKey: 'compass.storyTag', rgb: '37 244 238',
    span: 'xl:col-span-2',
    dir: '',
    media: 'aspect-[16/10] w-full border-b sm:aspect-auto sm:min-h-[260px] sm:flex-1 xl:min-h-[200px]',
    body: `${PHONE_BODY} sm:flex-none sm:p-6`,
  },
  // ads green #22c55e
  {
    id: 'ads', nameKey: 'compass.ads', tagKey: 'compass.adsTag', rgb: '34 197 94',
    span: 'xl:col-span-3',
    dir: 'xl:flex-row',
    media: 'aspect-[16/10] w-full border-b sm:aspect-auto sm:h-[260px] xl:h-auto xl:w-[38%] xl:border-b-0 xl:border-r',
    body: `${PHONE_BODY} sm:flex-1 sm:p-6 xl:p-7`,
    title: 'lg:text-[27px]',
  },
  // storybook stop coral
  {
    id: 'storybook', nameKey: 'compass.storybook', tagKey: 'compass.storybookTag', rgb: '247 122 100', isNew: true, light: true,
    span: 'sm:col-span-2 xl:col-span-3',
    // Tablet: a wide card with the picture beside the copy. Desktop: it
    // shares a row with the taller ads card, so the picture goes on top as a
    // banner that takes up the extra height instead of leaving a hole in the
    // copy. The picture lays itself out for each of those shapes
    // (StorybookVisual).
    dir: 'sm:flex-row-reverse xl:flex-col',
    media: 'aspect-[16/10] w-full border-b sm:aspect-auto sm:min-h-[260px] sm:w-[42%] sm:flex-none sm:border-b-0 sm:border-l xl:min-h-[170px] xl:w-auto xl:flex-1 xl:border-b xl:border-l-0',
    body: `${PHONE_BODY} sm:flex-1 sm:p-7 xl:flex-none`,
    title: 'sm:text-[26px] xl:text-[27px]',
  },
  // ChopBox teal #4FC5CD: the free editor, a full-width card (owner 2026-09-29:
  // its own ad on the landing page). It opens the editor itself, not a stop.
  {
    id: 'chopbox', nameKey: 'compass.chopbox', tagKey: 'compass.chopboxTag', rgb: '79 197 205', isNew: true,
    span: 'sm:col-span-2 xl:col-span-6',
    dir: 'sm:flex-row',
    media: 'aspect-[16/10] w-full border-b sm:aspect-auto sm:min-h-[300px] sm:w-[52%] sm:flex-none sm:border-b-0 sm:border-r',
    body: `${PHONE_BODY} sm:flex-1 sm:p-7 lg:p-9`,
    title: 'sm:text-[26px] lg:text-[32px]',
  },
  // Quick tools, copper #E8915F (the logo's CHOP SHOP): the five pay-per-file
  // tools, full width like ChopBox, opening /tools (owner 2026-10-16).
  {
    id: 'quick', nameKey: 'hero.welcome.cards.quick.name', tagKey: 'hero.welcome.cards.quick.tag', rgb: '232 145 95', isNew: true,
    span: 'sm:col-span-2 xl:col-span-6',
    dir: 'sm:flex-row-reverse',
    media: 'aspect-[16/10] w-full border-b sm:aspect-auto sm:min-h-[300px] sm:w-[52%] sm:flex-none sm:border-b-0 sm:border-l',
    body: `${PHONE_BODY} sm:flex-1 sm:p-7 lg:p-9`,
    title: 'sm:text-[26px] lg:text-[32px]',
  },
  // rap violet #a78bfa
  {
    id: 'rap', nameKey: 'compass.rap', rgb: '167 139 250', comingSoon: true,
    span: 'sm:col-span-2 xl:col-span-6',
    dir: 'sm:flex-row-reverse',
    media: 'h-[176px] w-full border-b sm:h-auto sm:min-h-[220px] sm:w-[46%] sm:flex-none sm:border-b-0 sm:border-l lg:w-[50%]',
    body: `${PHONE_BODY} sm:flex-1 sm:p-7 lg:p-8`,
    title: 'sm:text-[26px]',
  },
];

// Feature chips per tool (hero.welcome.chips.<id>.<n>). The storybook has
// two, and on desktops they share one row (2026-09-27): its card shares a row
// with the ads card, and a taller storybook card would stretch the ads card
// and open a hole between its credit ladder and its button.
const CHIP_COUNT: Partial<Record<ChooserToolId, number>> = { corrido: 3, story: 3, storybook: 2, chopbox: 3, quick: 3 };

/**
 * Where each equalizer bar rests: the frame of the tc-wave keyframes (35% up
 * to 100% and back over 1.3 s, ease-in-out) that its nth-child delay starts
 * it on. At rest the bars hold that frame; hover starts the keyframes from
 * exactly there (ToolChooser.css).
 */
function waveRest(n: number): number {
  if (n % 7 === 0) return 0.718; // -0.95 s
  if (n % 5 === 0) return 0.992; // -0.7 s
  if (n % 3 === 0) return 0.872; // -0.45 s
  if (n % 2 === 0) return 0.478; // -0.2 s
  return 0.35;
}

/**
 * Equalizer bars. They move only while their card is hovered (round 5,
 * 2026-09-25): a running transform animation makes every bar its own GPU
 * layer, which also forces the card's grain and ring above them onto the
 * compositor. The corrido beat was 24 layers for as long as the page lived,
 * and rap's "paused" teaser still 18. `still` bars never move (rap is locked).
 */
function Bars({ heights, className, still }: { heights: number[]; className: string; still?: boolean }) {
  return (
    <span className={`flex items-end justify-center ${still ? '' : 'tc-wave'} ${className}`}>
      {heights.map((h, i) => (
        <span
          key={i}
          className="block flex-1 origin-bottom rounded-full"
          style={{ height: `${h}%`, transform: `scaleY(${waveRest(i + 1)})` }}
        />
      ))}
    </span>
  );
}

interface ClipMedia {
  src: string;
  poster: string;
  /** Loop only the clip's first N seconds (see the story clip below). */
  loopEnd?: number;
}

/** The engine's own output for each card: a muted loop and its first frame. */
const CARD_MEDIA = {
  corrido: { src: '/promo/corrido_belico.mp4', poster: '/promo/poster_belico.jpg' },
  // The story render cuts at 3.83s from the captioned maple tree to a plain
  // blue sky, which in a small card phone read as a blank screen for half of
  // every loop (2026-09-24 critique). The card loops the tree part only; the
  // story pitch still plays the whole clip.
  story: { src: '/promo/story_clip_1.mp4', poster: '/promo/poster_story_1.jpg', loopEnd: 3.7 },
  ads: { src: '/promo/ad_energy_spot.mp4', poster: '/promo/poster_ad_energy.jpg' },
} satisfies Record<string, ClipMedia>;

// The first scroll into the bento ran at ~20 fps with 250-500 ms frames
// (round 2 critique, 2026-09-24): three clips fetched, set up a decoder and
// started on the very frame their cards began to rise. So the work is spread
// out. Each clip loads (paused, poster showing) in an idle moment while the
// bento is still up to a screen away; it starts only once its card has
// finished arriving; and first starts are spaced CLIP_STAGGER_MS apart.
//
// And never while the page moves (round 4 critique, 2026-09-25): a clip's
// first frame is where the compositor builds its video program, the single
// longest frame of the first scroll (0.6-1.4 s on a cold shader cache). A
// start that comes due mid-scroll waits until the page has settled
// (BentoScroll).
//
// Strictly one at a time (round 5, 2026-09-25). A fixed 280 ms gap still let
// two first frames pile onto one busy GPU on a slow machine. Starts now form
// a queue: the next clip is not asked to play until the one before it has
// put its first frame on screen (or CLIP_FIRST_FRAME_MAX_MS has passed),
// then the gap, then a still page and an idle moment. A clip only joins the
// queue once at least half of it is on screen, and one that has scrolled
// away by its turn is skipped.
const CLIP_STAGGER_MS = 280;
/** Longest a start holds the queue while waiting for its first frame. */
const CLIP_FIRST_FRAME_MAX_MS = 1500;
/** Share of a clip that has to be on screen before it starts. */
const CLIP_START_RATIO = 0.5;
/** Warm-ups (decoder set-up, first frame decoded behind the poster) are
 *  spread this far apart too. */
const CLIP_WARM_GAP_MS = 350;

interface ClipJob {
  /** Starts the clip if it still should; settles once its first frame is up. */
  run: () => Promise<void> | void;
  cancelled: boolean;
}
const clipQueue: ClipJob[] = [];
let clipTurn: { job: ClipJob; release: () => void } | null = null;

function pumpClips() {
  if (clipTurn) return;
  let job = clipQueue.shift();
  while (job?.cancelled) job = clipQueue.shift();
  if (!job) return;
  const current = job;
  let undo: (() => void) | null = null;
  let timer = 0;
  let over = false;
  const release = () => {
    if (over) return;
    over = true;
    undo?.();
    window.clearTimeout(timer);
    clipTurn = null;
    pumpClips();
  };
  clipTurn = { job: current, release };
  const attempt = () => {
    let settled = false;
    const unsettle = afterScrollSettles(() => {
      settled = true;
      undo = whenIdle(() => {
        undo = null;
        if (over) return;
        // The page started moving again while this waited for idle time.
        if (isPageScrolling()) {
          attempt();
          return;
        }
        let first: Promise<void> | void;
        try { first = current.run(); } catch { first = undefined; }
        // Skipped (scrolled away, unmounted): the next one goes at once.
        if (!first) {
          release();
          return;
        }
        void first.then(() => {
          if (!over) timer = window.setTimeout(release, CLIP_STAGGER_MS);
        });
      }, 600);
    });
    if (!settled) undo = unsettle;
  };
  attempt();
}

function queueClipStart(run: ClipJob['run']): () => void {
  const job: ClipJob = { run, cancelled: false };
  clipQueue.push(job);
  pumpClips();
  return () => {
    job.cancelled = true;
    const i = clipQueue.indexOf(job);
    if (i >= 0) clipQueue.splice(i, 1);
    if (clipTurn?.job === job) clipTurn.release();
  };
}

/** Settles once `video` has put a frame on screen after `started` (its
 *  play() promise), when that play is refused, or after the cap. */
function firstFrame(video: HTMLVideoElement, started: Promise<void> | undefined): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.clearTimeout(cap);
      video.removeEventListener('playing', onPlaying);
      resolve();
    };
    const cap = window.setTimeout(finish, CLIP_FIRST_FRAME_MAX_MS);
    // requestVideoFrameCallback fires when a frame is handed to the
    // compositor; `playing` (a little earlier) stands in where it is missing.
    const onPlaying = () => finish();
    if (typeof video.requestVideoFrameCallback === 'function') video.requestVideoFrameCallback(finish);
    else video.addEventListener('playing', onPlaying);
    started?.catch(finish);
  });
}

let nextWarmAt = 0;
/** Books a warm-up slot CLIP_WARM_GAP_MS after the last one; returns the cancel. */
function bookWarmSlot(fn: () => void): () => void {
  const now = performance.now();
  const at = Math.max(now, nextWarmAt);
  nextWarmAt = at + CLIP_WARM_GAP_MS;
  const id = window.setTimeout(fn, at - now);
  return () => window.clearTimeout(id);
}

/**
 * A muted, looping card clip that plays only while it is on screen. Nothing
 * downloads until the bento is near (preload none + poster), reduced-motion
 * visitors keep the poster, and a clip that fails to load falls back to the
 * poster rather than a broken video box.
 */
function CardClip({ src, poster, loopEnd, className = '' }: ClipMedia & { className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);

  // A shorter loop than the file: jump back to the start before `loopEnd`.
  // Checked on every decoded frame where the browser can (Chrome, Safari
  // 15.4+), so the cut never flashes; `timeupdate` (about 4 a second) is the
  // fallback, so it jumps a little early rather than a frame late.
  useEffect(() => {
    const video = ref.current;
    if (!video || !loopEnd) return undefined;
    if (typeof video.requestVideoFrameCallback === 'function') {
      let handle = 0;
      const onFrame: VideoFrameRequestCallback = (_now, meta) => {
        if (meta.mediaTime >= loopEnd) video.currentTime = 0;
        handle = video.requestVideoFrameCallback(onFrame);
      };
      handle = video.requestVideoFrameCallback(onFrame);
      return () => video.cancelVideoFrameCallback?.(handle);
    }
    const onTime = () => {
      if (video.currentTime >= loopEnd - 0.2) video.currentTime = 0;
    };
    video.addEventListener('timeupdate', onTime);
    return () => video.removeEventListener('timeupdate', onTime);
  }, [loopEnd, failed]);

  // Warm-up: fetch and decode the opening frames while paused, in an idle
  // moment, once the bento is within a screen. The poster stays up (a paused
  // clip that has never played keeps showing it), and the first play() later
  // has nothing left to set up. The three warm-ups are spaced apart and
  // never run while the page scrolls: setting up a decoder is work for the
  // same GPU process that draws the scroll. Skipped where the element cannot
  // play MP4 at all (jsdom) and for reduced motion, which never plays.
  useEffect(() => {
    const video = ref.current;
    if (!video || prefersReducedMotion() || typeof IntersectionObserver === 'undefined') return undefined;
    if (typeof video.canPlayType !== 'function' || !video.canPlayType('video/mp4')) return undefined;
    let cancel: (() => void) | null = null;
    const warm = () => {
      cancel = null;
      if (video.readyState > 0 || !video.paused) return;
      if (isPageScrolling()) {
        cancel = afterScrollSettles(() => { cancel = whenIdle(warm); });
        return;
      }
      video.preload = 'auto';
      // Chrome resumes a deferred load when preload changes; Safari needs
      // the explicit load().
      if (video.networkState !== video.NETWORK_LOADING) {
        try { video.load(); } catch { /* the first play() loads it instead */ }
      }
    };
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      cancel = bookWarmSlot(() => { cancel = whenIdle(warm); });
    }, { rootMargin: '100% 0px' });
    io.observe(video);
    return () => {
      io.disconnect();
      cancel?.();
    };
  }, [src, failed]);

  useEffect(() => {
    const video = ref.current;
    if (!video || prefersReducedMotion()) return undefined;
    const play = (): Promise<void> | undefined => {
      try {
        const p = video.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => { /* autoplay refused: the poster stays */ });
          return p;
        }
      } catch { /* no media support: the poster stays */ }
      return undefined;
    };
    if (typeof IntersectionObserver === 'undefined') {
      play();
      return undefined;
    }
    let visible = false;
    let started = false;
    let waiting = false;
    let alive = true;
    let cancelSlot: (() => void) | null = null;
    const start = () => {
      if (started) {
        play();
        return;
      }
      // Its card's entrance first, then its turn in the queue, then the
      // first frame. With no reveal managing the card (tests, reduced
      // motion) the gate is null and the clip plays straight away.
      const gate = whenRevealed(video);
      if (!gate) {
        started = true;
        play();
        return;
      }
      if (waiting) return;
      waiting = true;
      void gate.then(() => {
        if (!alive) return;
        // The handle stays until unmount: leaving mid-turn frees the queue.
        cancelSlot = queueClipStart(() => {
          waiting = false;
          if (!alive || !visible) return undefined;
          started = true;
          return firstFrame(video, play());
        });
      });
    };
    // Starts once half of it is on screen; pauses only once it has left the
    // screen entirely, so a clip at the fold does not stutter on and off.
    const io = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      // An entry without a ratio (a minimal observer) counts as on screen.
      const ratio = typeof e.intersectionRatio === 'number' ? e.intersectionRatio : 1;
      visible = e.isIntersecting && ratio >= CLIP_START_RATIO - 0.001;
      if (visible) start();
      else if (!e.isIntersecting) video.pause();
    }, { threshold: [0, CLIP_START_RATIO] });
    io.observe(video);
    return () => {
      alive = false;
      cancelSlot?.();
      io.disconnect();
    };
  }, [src, failed]);

  if (failed) return <img src={poster} alt="" loading="lazy" className={className} />;
  // The poster is also the element's own background (round 4 critique,
  // 2026-09-25): between play() dropping the poster and the compositor
  // drawing the first frame, a busy GPU once left the corrido picture empty
  // for a frame or more. Now the same picture shows through that gap; a
  // playing clip covers its box, so it is never seen otherwise. Callers
  // match its position to their object-position.
  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      onError={() => setFailed(true)}
      className={`bg-cover bg-no-repeat ${className}`}
      style={{ backgroundImage: `url(${poster})` }}
      data-testid="chooser-clip"
    />
  );
}

/**
 * A 9:16 clip in a phone-shaped frame over a blurred copy of its own poster,
 * for the tools whose output is vertical (a cover crop would cut the captions
 * or the product in half). `frame` carries the glow and hover motion.
 *
 * `rise` (2026-09-24): a centred phone in a wide band came out ~115px wide, a
 * small screen in a big box. The rising phone is sized to the band's height
 * plus a fifth and stands up from the card's lower edge, cropped by it like a
 * device on a stage; its captions sit mid-screen, well inside what shows. The
 * ads phone stays whole and centred: its product runs to the frame's foot.
 *
 * The ads spot opens on its title right at the top of the frame, where the
 * notch sat across it ("FaceSh▬ChopShop", round 2 critique, 2026-09-24). So
 * the whole phone's screen starts under the notch, like a real phone's top
 * bezel, pinned to the clip's top edge; the few px it loses come off the
 * clip's empty floor. The rising phone keeps a full screen: its captions sit
 * mid-frame.
 *
 * The rising phone is also capped by its band's width (tc-phone--rise): on
 * the tablet pair the TikTok band grew tall and narrow, and a phone sized
 * from its height alone came out as wide as the card.
 */
function PhoneClip({ media, frame, frameStyle, rise = false }: {
  media: ClipMedia;
  frame: string;
  frameStyle?: CSSProperties;
  rise?: boolean;
}) {
  return (
    <>
      <img src={media.poster} alt="" loading="lazy" className="absolute inset-0 h-full w-full scale-125 object-cover opacity-40 blur-2xl" />
      <span className="tc-phone-stage absolute inset-0 block">
        <span
          className={[
            'tc-phone absolute left-1/2 block aspect-[9/16] -translate-x-1/2 transition-transform duration-700 ease-expo-out',
            rise ? 'tc-phone--rise origin-bottom' : 'top-1/2 h-[86%] max-h-[330px] -translate-y-1/2',
            frame,
          ].join(' ')}
          style={frameStyle}
        >
          <span
            className={[
              'absolute overflow-hidden bg-black',
              rise ? 'inset-[3px] rounded-[17px]' : 'inset-x-[3px] bottom-[3px] top-[15px] rounded-b-[17px] rounded-t-[7px]',
            ].join(' ')}
          >
            <CardClip {...media} className={`h-full w-full object-cover ${rise ? 'bg-center' : 'object-top bg-top'}`} />
          </span>
          <span className="absolute left-1/2 top-[7px] h-[5px] w-[30%] -translate-x-1/2 rounded-full bg-black/80 ring-1 ring-white/10" />
        </span>
      </span>
      {/* The rising phone sinks into the card under a short fade. */}
      {rise && <span className="absolute inset-x-0 bottom-0 h-[22%] bg-gradient-to-t from-[rgb(13,15,20)] to-transparent" />}
    </>
  );
}

function CorridoVisual() {
  const bars = [30, 55, 80, 45, 65, 95, 50, 70, 35, 85, 60, 40, 75, 55, 90, 45, 30, 65, 80, 50, 35, 60, 72, 44];
  return (
    <>
      {/* Phones show a 16:10 slice of this vertical clip: 38% keeps the
          Guadalajara sign and the road under it. */}
      <CardClip
        {...CARD_MEDIA.corrido}
        className="tc-zoom absolute inset-0 h-full w-full object-cover object-[center_38%] bg-[position:center_38%] sm:object-[center_42%] sm:bg-[position:center_42%]"
      />
      <span className="absolute inset-0 bg-[radial-gradient(circle_at_78%_12%,rgba(244,190,82,0.30),transparent_55%)]" />
      {/* Phones: the picture sits on top, so its bottom melts into the card.
          sm+: it sits to the right of the copy, so its left edge does. */}
      <span className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[rgb(13,15,20)] to-transparent sm:hidden" />
      <span className="absolute inset-y-0 left-0 hidden w-2/5 bg-gradient-to-r from-[rgb(13,15,20)] via-[rgba(13,15,20,0.45)] to-transparent sm:block" />
      <span className="absolute inset-x-0 bottom-0 hidden h-2/5 bg-gradient-to-t from-black/75 to-transparent sm:block" />
      {/* A beat under the skyline: the card is for music. */}
      <Bars
        heights={bars}
        className="absolute inset-x-6 bottom-6 hidden h-12 gap-[3px] opacity-85 sm:flex [&>span]:bg-[linear-gradient(180deg,#f8d48a,#d99a2b)]"
      />
    </>
  );
}

function StoryVisual() {
  return (
    <>
      <span className="absolute inset-0 bg-[radial-gradient(circle_at_18%_16%,rgba(37,244,238,0.30),transparent_55%),radial-gradient(circle_at_84%_86%,rgba(254,44,85,0.32),transparent_55%),linear-gradient(160deg,#0b0d12,#12141a)]" />
      {/* The story engine's real captioned render, in a phone with the TikTok
          cyan/pink offset edge. */}
      <PhoneClip
        rise
        media={CARD_MEDIA.story}
        frame="tc-lean-story shadow-[-5px_-4px_0_rgba(37,244,238,0.85),5px_4px_0_rgba(254,44,85,0.85),0_24px_50px_rgba(0,0,0,0.55)]"
      />
    </>
  );
}

/** The cheapest thing the ads tool sells, from its credit ladder: a spot, a
 *  set of image ads at the smallest count, or a package. */
function lowestAdsCredits(config: AdsConfig = ADS_FALLBACK_CONFIG): number {
  const minStills = Math.min(...config.still_counts);
  return Math.min(
    ...Object.values(config.credits.spot),
    config.credits.still * minStills,
    ...Object.values(config.credits.package),
  );
}

function AdsVisual() {
  return (
    <>
      <span className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(34,197,94,0.30),transparent_55%),radial-gradient(circle_at_85%_85%,rgba(250,204,21,0.22),transparent_55%),linear-gradient(160deg,#0b120e,#11140f)]" />
      {/* The ads tool's real spot of the owner's FaceShot-ChopShop can, in
          the tool's green glow. */}
      <PhoneClip
        media={CARD_MEDIA.ads}
        frame="tc-lean-ads"
        frameStyle={{ boxShadow: '0 0 0 1px rgba(34,197,94,0.6), 0 0 34px -6px rgba(34,197,94,0.8), 0 24px 50px rgba(0,0,0,0.55)' }}
      />
    </>
  );
}

/**
 * What the storybook makes, at a glance (owner, 2026-09-27: "it's not quite
 * clear just by looking at it what it provides ... actually putting their
 * faces in the story"): two sample photos, an arrow, and the cover the
 * storybook engine drew from them: the one drawing the owner approved for the
 * site (promo.ts), and nothing else drawn. Mia and her mom are SYNTHETIC
 * sample people, so the photos sit over a "Sample photos" label, as they do
 * everywhere on the site.
 *
 * The band this fills changes shape a lot: a 16:10 strip on phones, a tall
 * column beside the copy on tablets, a ~3.5:1 banner on desktops. So the
 * picture is a scene of fixed proportions fitted inside the band, laid out
 * three ways by a container query on the band's shape (tc-sb-* in
 * ToolChooser.css). Every picture is a small webp (5-20 KB), lazy, with a
 * srcset: the whole card costs about 35 KB.
 */
function StorybookVisual({ samplePhotos }: { samplePhotos: string }) {
  return (
    <>
      <span className="absolute inset-0 bg-[linear-gradient(135deg,#fff3dd_0%,#ffe1ea_50%,#dcecff_100%)]" />
      {/* Two soft lights on the paper. Drawn as the profile of a 160px disc
          under a 40px blur, rather than blurring one (round 5, 2026-09-25):
          the same glow without a blur filter to rasterise every time the
          card is painted, and none of the WebKit blur-layer trouble the
          Features tiles had. */}
      <span
        className="absolute -right-[120px] -top-32 h-80 w-80"
        style={{ background: 'radial-gradient(closest-side, rgb(255 255 255 / 0.6), rgb(255 255 255 / 0.55) 25%, rgb(255 255 255 / 0.32) 50%, rgb(255 255 255 / 0.1) 75%, transparent)' }}
      />
      <span
        className="absolute -bottom-[136px] -left-28 h-80 w-80"
        style={{ background: 'radial-gradient(closest-side, hsl(45 95% 80% / 0.69), hsl(45 95% 80% / 0.63) 25%, hsl(45 95% 80% / 0.37) 50%, hsl(45 95% 80% / 0.11) 75%, transparent)' }}
      />
      <span className="tc-sb-stage absolute inset-0 block">
        <span className="tc-sb-scene block">
          <span className="tc-sb-print tc-sb-print--ana">
            <PromoImg image={SAMPLE_PHOTOS.ana} sizes="90px" />
          </span>
          <span className="tc-sb-print tc-sb-print--mia">
            <PromoImg image={SAMPLE_PHOTOS.mia} sizes="110px" />
          </span>
          <span className="tc-sb-tag">{samplePhotos}</span>
          <span className="tc-sb-books">
            <span className="tc-sb-cover tc-sb-front">
              <PromoImg image={APPROVED_DRAWING} sizes="(min-width: 640px) 260px, 45vw" />
            </span>
          </span>
          <span className="tc-sb-arrow">
            <ArrowRight strokeWidth={2.75} />
          </span>
        </span>
      </span>
    </>
  );
}

function RapVisual({ soon, metaId }: { soon: string; metaId: string }) {
  const bars = [28, 46, 62, 38, 70, 52, 34, 58, 44, 26, 40, 30, 55, 36, 48, 64, 32, 50];
  return (
    <>
      <span className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(167,139,250,0.30),transparent_60%),linear-gradient(160deg,#131021,#0c0b12)]" />
      <Bars
        heights={bars}
        still
        className="absolute inset-x-5 bottom-0 h-[70%] gap-1 opacity-45 blur-[1.5px] sm:gap-1.5 [&>span]:max-w-2 [&>span]:bg-[linear-gradient(180deg,#c4b5fd,#6d5bb3)]"
      />
      <span className="absolute inset-0 bg-[linear-gradient(180deg,transparent_30%,rgba(12,11,18,0.75))]" />
      {/* The coming-soon lock: a sealed medallion and the label, over the
          dimmed teaser. The label is the card's meta line for screen
          readers too (aria-describedby reaches into aria-hidden). */}
      <span className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <span className="tc-seal relative flex h-14 w-14 items-center justify-center rounded-full border border-white/15 bg-[#15121f]/90 text-[#c4b5fd]">
          <Mic2 className="h-6 w-6" />
          <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-[#221c33] text-white/85">
            <Lock className="h-3 w-3" />
          </span>
        </span>
        <span
          id={metaId}
          className="rounded-full border border-[#a78bfa]/35 bg-[#15121f]/85 px-3.5 py-1.5 text-[11.5px] font-bold uppercase tracking-[0.2em] text-[#d8ccff]"
        >
          {soon}
        </span>
      </span>
    </>
  );
}

/**
 * The ads credit ladder, as bars against the dearest rung. Its header carries
 * the card's "from N credits" line (the lowest rung) instead of a separate
 * meta line under the ladder: the same fact twice cost the ads card a phone
 * screen of height (2026-09-24).
 */
function AdsLadder({ metaId }: { metaId: string }) {
  const { t } = useTranslation();
  const c = ADS_FALLBACK_CONFIG.credits;
  // "Image ads × 8" must never break before the 8 (it did in Spanish at
  // 320px): non-breaking spaces round the ×.
  const rows = [
    { label: t('pitch.ads.tiers.stills8').replace(/ × /g, ' × '), credits: c.still * 8 },
    { label: t('pitch.ads.tiers.spot15'), credits: c.spot['15'] },
    { label: t('pitch.ads.tiers.spot30'), credits: c.spot['30'] },
    { label: t('pitch.ads.tiers.spot60'), credits: c.spot['60'] },
  ];
  const max = Math.max(...rows.map((r) => r.credits));
  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-3.5 max-[359px]:px-3 sm:p-4">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 sm:mb-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:text-[11.5px] sm:tracking-[0.16em]">{t('pitch.ads.tiersTitle')}</p>
        <p className="flex items-center gap-1.5 text-[13.5px] font-semibold leading-tight text-foreground sm:text-[14px]">
          <Coins className="h-4 w-4 shrink-0 text-[rgb(var(--card-ink))]" aria-hidden="true" />
          <span id={metaId}>{t('pitch.ads.creditsFrom', { count: lowestAdsCredits() })}</span>
        </p>
      </div>
      <ul className="space-y-2 sm:space-y-2.5">
        {rows.map((row) => (
          <li key={row.label} className="tc-ladder-row">
            <div className="mb-1 flex justify-between gap-3 text-[13px] leading-tight max-[359px]:gap-2 max-[359px]:text-[12.5px] sm:mb-1.5">
              <span className="min-w-0 text-foreground/75">{row.label}</span>
              <span className="shrink-0 font-semibold tabular-nums text-foreground">{t('pitch.ads.credits', { count: row.credits })}</span>
            </div>
            <div className="h-[3px] overflow-hidden rounded-full bg-white/10 sm:h-1">
              <div
                className="tc-rung h-full rounded-full bg-[linear-gradient(90deg,#22c55e,#facc15)]"
                style={{ width: `${Math.max(8, Math.round((row.credits / max) * 100))}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * "Free preview · No account needed" may break at its " · " only: word by
 * word it left "needed" alone at 320px, and in Spanish split "No" from
 * "necesitas cuenta" (round 2 critique, 2026-09-24). Non-breaking spaces hold
 * each clause together; the words, and the tests' match on them, are as
 * written.
 */
/**
 * The quick tools' card picture: each tool's real sample (scripts/
 * quicktools-samples.cjs) with its name and price per file, as a tiled wall.
 */
// The quick tools' stage card (owner 2026-10-16: not the same pictures as the
// quick-tools row below): what you walk away with, drawn as finished files on
// a desk light, each with its price. No photos, so nothing repeats.
const QUICK_FILES: Record<QuickToolKey, string> = {
  captions: 'captions.mp4', hooks: 'hooks.zip', canvas: 'spotify-loop.mp4', faceless: 'video.zip', vows: 'vows.pdf', tapes: 'family-page',
};
const QUICK_ICONS: Record<string, typeof Captions> = {
  captions: Captions, clapperboard: Clapperboard, scissors: Scissors, repeat: Repeat, waveform: AudioWaveform, film: Film,
};

function QuickToolsVisual() {
  const { t } = useTranslation();
  const prices = useQuickToolPrices();
  const order = quickTools.order as QuickToolKey[];
  return (
    <div className="absolute inset-0 overflow-hidden bg-[radial-gradient(120%_90%_at_18%_0%,rgba(232,145,95,0.32),transparent_55%),radial-gradient(90%_80%_at_100%_100%,rgba(45,212,191,0.16),transparent_60%),#0b0d12]">
      <div aria-hidden className="absolute inset-0 opacity-[0.07] [background-image:linear-gradient(rgba(255,255,255,.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.6)_1px,transparent_1px)] [background-size:28px_28px]" />
      {/* Phones: a two-column grid of chips (the picture area is short);
          sm+: the staggered stack of files. */}
      <ul className="relative grid h-full grid-cols-2 content-center gap-1.5 px-3 pb-3 pt-11 sm:flex sm:flex-col sm:justify-center sm:gap-2 sm:px-6 sm:py-4">
        {order.map((key, i) => {
          const Icon = QUICK_ICONS[quickTools.tools[key].icon] || Captions;
          return (
            <li
              key={key}
              style={{ '--ml': `${(i % 2) * 7 + i * 2}%`, '--mr': `${(4 - i) * 2}%` } as CSSProperties}
              className="flex items-center gap-2 rounded-xl sm:ml-[var(--ml)] sm:mr-[var(--mr)] border border-white/10 bg-white/[0.06] px-2.5 py-1.5 shadow-[0_14px_30px_-18px_rgba(0,0,0,0.9)] backdrop-blur-sm sm:gap-3 sm:rounded-2xl sm:px-3 sm:py-2"
            >
              <span className="hidden h-7 w-7 shrink-0 items-center justify-center rounded-lg sm:flex bg-[#E8915F]/15 text-[#F4BE52] ring-1 ring-inset ring-[#F4BE52]/30 sm:h-9 sm:w-9 sm:rounded-xl">
                <Icon className="h-4 w-4 sm:h-[18px] sm:w-[18px]" aria-hidden />
              </span>
              <span className="min-w-0 flex-1 sm:block">
                <span className="line-clamp-2 block text-[12px] font-bold leading-tight text-white sm:truncate sm:text-[13px]">{t(`qt.tools.${key}.name`)}</span>
                <span className="hidden truncate font-mono text-[11px] text-white/55 sm:block">{QUICK_FILES[key]}</span>
              </span>
              <span className="shrink-0 rounded-full bg-[#F4BE52] px-2.5 py-0.5 text-[12px] font-bold tabular-nums text-[#141006]">{money(prices[key])}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function keepClauses(text: string): string {
  return text.split(' · ').map((clause) => clause.replace(/ /g, ' ')).join(' · ');
}

function BentoCard({ card, onPick }:{ card: CardDef; onPick: (card: CardDef, e: MouseEvent<HTMLElement>) => void }) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  // The wide cards lean less: the same angle swings their far edge further.
  useTilt(cardRef, { max: card.comingSoon ? 2.5 : card.lead ? 4 : 5, lift: 6 });

  const descId = `tool-card-${card.id}-desc`;
  const metaId = `tool-card-${card.id}-meta`;
  const chips = Array.from({ length: CHIP_COUNT[card.id] ?? 0 }, (_, i) => t(`hero.welcome.chips.${card.id}.${i + 1}`));
  const light = !!card.light;

  let visual: ReactNode;
  if (card.id === 'corrido') visual = <CorridoVisual />;
  else if (card.id === 'story') visual = <StoryVisual />;
  else if (card.id === 'ads') visual = <AdsVisual />;
  else if (card.id === 'storybook') visual = <StorybookVisual samplePhotos={t('storybookPage.promo.samplePhotos')} />;
  else if (card.id === 'chopbox') visual = <ChopBoxVisual card />;
  else if (card.id === 'quick') visual = <QuickToolsVisual />;
  else visual = <RapVisual soon={t('pitch.rap.comingSoon')} metaId={metaId} />;

  // One short line above each card's CTA: the price or offer the site
  // already states for that tool, or what makes it different. (The ads card
  // carries its "from N credits" in the ladder header instead.)
  const meta: Partial<Record<ChooserToolId, { text: string; icon: ReactNode }>> = {
    story: { text: t('pitch.story.point2Title'), icon: <BadgeCheck className="h-[18px] w-[18px]" /> },
    storybook: { text: keepClauses(t('storybook.note')), icon: <Gift className="h-[18px] w-[18px]" /> },
    chopbox: { text: t('chopboxPitch.meta'), icon: <Scissors className="h-[18px] w-[18px]" /> },
    quick: { text: t('hero.welcome.cards.quick.meta'), icon: <Coins className="h-[18px] w-[18px]" /> },
  };
  const line = meta[card.id];

  // Touch gets a press instead of hover: the card gives a little under the
  // finger. Set on the node directly so a tap never re-renders the card.
  const press = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') e.currentTarget.dataset.pressed = '';
  };
  const release = (e: PointerEvent<HTMLDivElement>) => {
    delete e.currentTarget.dataset.pressed;
  };

  const newBadge = t('hero.welcome.newBadge');
  // The cursor ring's word shows over the picture only (2026-09-24): over
  // the copy it sat on top of the words it was labelling ("Make a musOPEN
  // video"). The storybook's picture is cream, where the ring's white word
  // cannot be read, so there the ring just swells.
  const cursorWord = light
    ? ''
    : card.comingSoon
      ? t('immersiveBento.cursorPeek', { defaultValue: 'Peek' })
      : t('immersiveBento.cursorOpen', { defaultValue: 'Open' });

  return (
    <div
      ref={cardRef}
      onPointerDown={press}
      onPointerUp={release}
      onPointerCancel={release}
      onPointerLeave={release}
      className={[
        'tc-card relative flex h-full flex-col overflow-hidden rounded-[26px]',
        card.dir,
        light ? 'tc-light' : card.comingSoon ? 'tc-surface tc-soon' : 'tc-surface',
      ].join(' ')}
      style={{ '--card-rgb': card.rgb } as CSSProperties}
    >
      <span className="tc-spot" aria-hidden="true" />
      <span className="tc-glare" aria-hidden="true" />
      {/* Ambient accent glow in the corner: a soft radial gradient rather
          than a blurred disc, so a phone never has to rasterise a big blur. */}
      <span
        aria-hidden="true"
        className="tc-glow pointer-events-none absolute -left-32 -top-32 h-80 w-80"
        style={{ background: `radial-gradient(closest-side, rgb(${card.rgb} / ${light ? 0.3 : 0.2}), transparent)` }}
      />
      {/* The picture takes its own clicks and taps (the same pick as the
          title button), so the cursor can tell it apart from the copy. It
          stays out of the accessibility tree: the button is the one control
          screen readers and the keyboard use. */}
      <div
        aria-hidden="true"
        onClick={(e) => onPick(card, e)}
        data-cursor={cursorWord}
        className={[
          'tc-media relative shrink-0 cursor-pointer overflow-hidden',
          light ? 'border-black/[0.07]' : 'border-white/[0.08]',
          card.media,
        ].join(' ')}
      >
        <div className="tc-parallax absolute inset-0">{visual}</div>
        {/* The NEW sticker sits on the artwork, where it never has to share a
            line with the long tag chip (it wrapped onto a line of its own on
            phones). Screen readers get it beside the tag instead. */}
        {card.isNew && (
          <span className="absolute left-4 top-4 z-[1] inline-flex items-center gap-1.5 rounded-full bg-[#F77A64] px-3 py-1.5 text-[11.5px] font-extrabold uppercase leading-none tracking-[0.14em] text-[#1a0f0c] shadow-[0_8px_22px_-6px_rgba(247,122,100,0.9)]">
            <Sparkles className="h-3.5 w-3.5" />
            {newBadge}
          </span>
        )}
      </div>

      {/* The body is positioned with a z-index, so the title button's ::after
          covers the whole copy column (chips, price, CTA) as one target and
          paints above everything in it; the picture beside or above it is
          its own target. */}
      <div className={`relative z-[3] flex min-w-0 flex-col ${card.body}`}>
        {card.tagKey && (
          <div className={`mb-3 flex flex-wrap items-center gap-2 sm:mb-[18px] ${card.lead ? 'xl:mb-6' : ''}`}>
            <span className="tc-tag">
              <span className="tc-tag-dot" aria-hidden="true" />
              {t(card.tagKey)}
            </span>
            {card.isNew && <span className="sr-only">{newBadge}</span>}
          </div>
        )}

        <h3
          className={[
            'text-balance font-bold',
            card.lead
              ? 'text-[26px] leading-[1.06] tracking-[-0.025em] sm:text-[36px] sm:leading-[1.05] lg:text-[50px]'
              : `text-[22px] leading-[1.15] tracking-[-0.01em] ${card.title ?? 'lg:text-[23px]'}`,
            light ? 'text-[#1d1512]' : card.comingSoon ? 'text-foreground/85' : 'text-foreground',
          ].join(' ')}
        >
          <button
            type="button"
            onClick={(e) => onPick(card, e)}
            aria-describedby={`${descId} ${metaId}`}
            className="-my-2.5 block w-full py-2.5 text-left outline-none after:absolute after:inset-0 after:z-[4] after:content-['']"
          >
            {t(card.nameKey)}
          </button>
        </h3>
        <p
          id={descId}
          className={[
            'mt-1.5 text-pretty text-[15.5px] leading-[1.5] sm:mt-2 sm:text-[16px] sm:leading-[1.55]',
            card.lead ? 'sm:mt-3 sm:max-w-[30rem] sm:text-[17px] lg:text-[18px] xl:mt-4' : card.title ? 'lg:text-[16px]' : 'lg:text-[15.5px]',
            card.comingSoon ? 'lg:max-w-md' : '',
            light ? 'text-[#56473f]' : 'text-muted-foreground',
          ].join(' ')}
        >
          {t(`hero.welcome.cards.${card.id}.desc`)}
        </p>

        {chips.length > 0 && (
          <ul className={`flex flex-wrap gap-1.5 sm:gap-2 ${card.lead ? 'mt-3.5 sm:mt-6 xl:mt-8' : 'mt-3.5 sm:mt-4'}`}>
            {chips.map((chip) => (
              <li key={chip} className={`tc-chip ${light ? 'tc-chip-light' : ''}`}>
                <Check className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--card-ink))]" aria-hidden="true" />
                {chip}
              </li>
            ))}
          </ul>
        )}

        {/* The ads ladder follows its pitch rather than riding at the foot
            with the button: when the row stretches the card, the room opens
            above the button instead of as a hole between the pitch and
            what it costs (round 3 critique, 2026-09-25). */}
        {card.id === 'ads' && (
          <div className="mt-4 sm:mt-5">
            <AdsLadder metaId={metaId} />
          </div>
        )}

        {/* The price, meta line and button sit together at the foot of the
            card, so when a row stretches a card the spare room opens above
            them rather than between a price and its button. */}
        <div className={`mt-auto flex flex-col ${card.lead ? 'pt-5 sm:pt-8' : card.id === 'ads' ? '' : 'pt-4 sm:pt-5'}`}>
          {card.lead ? (
            // The corrido price against what a crew costs: both are lines
            // the corrido pitch already carries. The price gets a row of its
            // own and the crew cost follows as one quiet sentence ("A crew
            // shoot runs $1,500 – $50,000+"). Round 2 critique, 2026-09-24:
            // side by side from sm up, the ~370px copy column broke the price
            // into "$99 per / finished video", the Spanish labels onto two
            // lines each, and the crew figure ran past the divider.
            <dl className="grid grid-cols-1 gap-y-2 border-t border-white/10 pt-4 sm:gap-y-2.5 sm:pt-5 xl:pt-6">
              <div className="min-w-0">
                <dt className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {t('pitch.corrido.introPriceLabel').replace(/:\s*$/, '')}
                </dt>
                <dd id={metaId} className="mt-1 text-balance text-[18px] font-bold leading-snug text-foreground sm:mt-1.5 sm:text-[20px] md:whitespace-nowrap xl:text-[22px]">
                  {t('pitch.corrido.introPriceValue', { price: INTRO_PRICE })}
                </dd>
              </div>
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
                <dt className="text-[15px] font-medium leading-snug text-muted-foreground">
                  {t('pitch.corrido.crewCostLabel')}
                </dt>
                <dd className="whitespace-nowrap text-[15px] font-semibold leading-snug text-foreground/60">
                  {t('pitch.corrido.crewCostValue')}
                </dd>
              </div>
            </dl>
          ) : line ? (
            <p className={`mt-4 flex items-center gap-2.5 border-t pt-3.5 first:mt-0 text-[15px] font-semibold leading-snug sm:mt-5 sm:pt-4 ${light ? 'border-black/10 text-[#3d302a]' : 'border-white/10 text-foreground/90'}`}>
              <span className="shrink-0 text-[rgb(var(--card-ink))]" aria-hidden="true">{line.icon}</span>
              <span id={metaId}>{line.text}</span>
            </p>
          ) : null}

          {/* Both pills grow rather than clip when a label needs two lines
              (Spanish at 320px did, inside a fixed-height pill); under 360px
              they also tighten so it rarely has to. */}
          {card.offline ? (
            <OfflineButton className="mt-3.5 sm:mt-4" />
          ) : card.comingSoon ? (
            <span className="tc-soon-cta mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-2.5 text-center text-[15px] font-bold leading-tight text-foreground/75 first:mt-0 max-[359px]:px-4 sm:mt-5 sm:max-w-[17rem] lg:min-h-11">
              <Lock className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="[text-wrap:balance]">{t(`hero.welcome.cards.${card.id}.soonCta`, { defaultValue: t(`hero.welcome.cards.${card.id}.cta`) })}</span>
            </span>
          ) : (
            <Magnetic strength={0.12} radius={24} className="mt-3.5 w-full sm:mt-4">
              <span
                className={[
                  'tc-cta relative flex min-h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-full px-5 py-2.5 text-[15.5px] font-bold leading-tight text-[#0b0d12] max-[359px]:gap-1.5 max-[359px]:px-4 max-[359px]:text-[14px]',
                  card.lead ? 'sm:min-h-[52px] sm:text-[16px] lg:min-h-12' : 'lg:min-h-11 lg:text-[15px]',
                ].join(' ')}
                style={{ background: `rgb(${card.rgb})` }}
              >
                <span className="tc-cta-label text-center [text-wrap:balance]">{t(`hero.welcome.cards.${card.id}.cta`)}</span>
                <ArrowRight className="tc-cta-arrow h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="tc-shine" aria-hidden="true" />
              </span>
            </Magnetic>
          )}
        </div>
      </div>
    </div>
  );
}

export function ToolChooser({ onSelect }: { onSelect: (tool: ChooserToolId) => void }) {
  const { t } = useTranslation();
  // TEMPORARILY OFFLINE, AS THE OWNER SAID IT (2026-10-16): every tool stays
  // open to look at and plan in; only the button that starts a paid job is
  // greyed and says it is temporarily offline for routine maintenance.
  // The pay-per-file tools are cards of their own below, with the others
  // (owner 2026-10-16: not one "quick tools" folder card).
  const cards = CARDS.filter((card) => card.id !== 'quick');
  const rootRef = useRef<HTMLElement>(null);
  useBentoReveal(rootRef);
  // No hover while the page scrolls under a resting mouse; clips wait for
  // the page to settle before their first frame (see BentoScroll).
  useScrollCalm(rootRef);
  // The cards' first raster happens while the hero is being read, not in
  // the first scroll (see BentoWarm).
  useBentoWarmup(rootRef);

  // The wipe in flight, if any. The chooser unmounting IS the new stop
  // arriving, so the cleanup is what tells the cover to lift. A layout
  // cleanup, so it runs in the commit that swaps the new stop in: a passive
  // one waited behind every effect the new stop starts (round 3 critique,
  // 2026-09-25: 230-400 ms of them on a mid-range phone).
  const wipeRef = useRef<BentoWipe | null>(null);
  const aliveRef = useRef(true);
  useLayoutEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      wipeRef.current?.release();
    };
  }, []);

  const pick = (card: CardDef, e: MouseEvent<HTMLElement>) => {
    // One pick at a time: a second tap while the disc opens is ignored.
    if (wipeRef.current) return;
    // A tool that is temporarily offline cannot be opened.
    if (card.offline) return;
    // ChopBox and the quick tools are their own pages (HeroSection), not stops, so no wipe.
    if (card.id === 'chopbox' || card.id === 'quick') {
      onSelect(card.id);
      return;
    }
    const box = e.currentTarget.closest('.tc-card')?.getBoundingClientRect();
    // A keyboard press has no pointer position: open from the card's centre.
    const fromPointer = e.detail > 0 && (e.clientX !== 0 || e.clientY !== 0);
    // Safari's click is not a PointerEvent: a fine pointer stands in.
    const pointerType = (e.nativeEvent as Partial<globalThis.PointerEvent>).pointerType;
    const wipe = box && box.height
      ? playBentoWipe(
        fromPointer ? { x: e.clientX, y: e.clientY } : { x: box.left + box.width / 2, y: box.top + box.height / 2 },
        card.rgb,
        {
          // Under the full cover (round 2 critique, 2026-09-24): rendering
          // the stop blocks the page for 0.2-0.6 s, so it waits until the
          // disc has covered the screen. Not a transition any more (round
          // 3, 2026-09-25): the disc runs on the compositor and nothing
          // shows under the cover, so yielding only let the hero's WebGL
          // frames and the card clips run between React's slices and made
          // the swap 1-2 s slower on a throttled phone. The chooser's
          // unmount releases the cover.
          onCovered: () => {
            if (aliveRef.current) onSelect(card.id);
          },
          onEnd: () => { wipeRef.current = null; },
          // While the stop is built the cover names the tool being opened.
          title: { name: t(card.nameKey), tag: card.tagKey ? t(card.tagKey) : undefined },
          mouse: fromPointer && (pointerType ? pointerType === 'mouse' : hasFinePointer()),
        },
      )
      : null;
    // No wipe (reduced motion, no Web Animations — tests): switch at once.
    if (!wipe) {
      onSelect(card.id);
      return;
    }
    wipeRef.current = wipe;
  };

  // mt-6 on phones, mt-10 on desktops (2026-09-24): the welcome stage above
  // already ends in its scroll cue and breathing room, and on a 390x664
  // iPhone or a 1280x800 laptop those pixels are what let the heading, not
  // just the eyebrow, peek above the fold.
  return (
    <section
      ref={rootRef}
      aria-labelledby="tool-chooser-title"
      className="tc-bento mx-auto mt-6 max-w-6xl scroll-mt-24 text-left sm:mt-16 lg:mt-10"
      data-testid="tool-chooser"
    >
      {/* The eyebrow uses the page's one eyebrow style (a short leading
          amber rule), the same as How it works, Get started and the FAQ. */}
      <header className="mb-6 sm:mb-10">
        <p data-reveal-head className="eyebrow">
          {t('immersiveBento.eyebrow', { defaultValue: 'The studio' })}
        </p>
        {/* Under 400px the heading is 28px, so it takes one line or two
            tight ones and the fold never slices through a line of it. */}
        <h2
          id="tool-chooser-title"
          data-reveal-head
          className="mt-3 text-balance text-[28px] font-bold leading-[1.04] tracking-[-0.025em] text-foreground min-[400px]:text-[32px] sm:mt-4 sm:text-[44px] lg:text-[56px]"
        >
          {t('hero.welcome.chooserTitle')}
        </h2>
      </header>
      {/* The Living Cover first, under "Choose what to make" and above the other studios (owner 2026-10-19: "it should
          go under choose what to make but above the other" - "currently our best fully functional no glitch tool"). */}
      <SpotifyLoopBand embedded />
      <ul className="grid gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-6">
        {cards.map((card) => (
          <li key={card.id} data-reveal className={`h-full ${card.span}`} data-testid={`tool-card-${card.id}`} data-coming-soon={card.comingSoon ? 'true' : undefined} data-offline={card.offline ? 'true' : undefined}>
            <BentoCard card={card} onPick={pick} />
          </li>
        ))}
      </ul>
      {/* The five pay-per-file tools, each its own card, in the same studio. */}
      <div className="mt-4 sm:mt-5" data-testid="stage-quick-tools">
        <QuickToolCards />
      </div>
    </section>
  );
}
